import { ColDef, GridOptions } from 'ag-grid-community';

/**
 * ISG Grid Configuration Interface
 * Standardized configuration for all AG-Grid instances
 */
export interface IsgGridConfig {
  /** Grid theme - locked to 'balham' for consistency */
  theme?: 'balham';

  /** Enable pagination */
  pagination?: boolean;

  /** Number of rows per page */
  pageSize?: number;

  /** Page size options for dropdown */
  pageSizeOptions?: number[];

  /** Row selection mode */
  rowSelection?: 'single' | 'multiple';

  /** Auto-size columns to fit container on grid ready */
  autoSizeColumns?: boolean;

  /** Enable column sorting */
  enableSorting?: boolean;

  /** Enable column filtering */
  enableFiltering?: boolean;

  /** Grid height (CSS value) */
  height?: string;

  /** Enable row hover highlighting */
  enableRowHover?: boolean;

  /** Show loading overlay while data loads */
  showLoadingOverlay?: boolean;

  /** Custom no-rows message */
  noRowsMessage?: string;

  /** Enable cell text selection */
  enableCellTextSelection?: boolean;

  /** Suppress horizontal scroll */
  suppressHorizontalScroll?: boolean;

  /** Enable animations */
  animateRows?: boolean;

  /** Enable row drag and drop management */
  rowDragManaged?: boolean;

  /** Header height in pixels (use 0 to hide headers) */
  headerHeight?: number;
}

/**
 * Default grid configuration
 * Applied to all grids unless overridden
 */
export const DEFAULT_GRID_CONFIG: IsgGridConfig = {
  theme: 'balham',
  pagination: true,
  pageSize: 10,
  pageSizeOptions: [10, 25, 50, 100],
  rowSelection: 'multiple',
  autoSizeColumns: true,
  enableSorting: true,
  enableFiltering: true,
  height: '400px',
  enableRowHover: true,
  showLoadingOverlay: true,
  noRowsMessage: 'No data available',
  enableCellTextSelection: false,
  suppressHorizontalScroll: false,
  animateRows: true
};

/**
 * Default column definition
 * Applied to all columns unless overridden
 */
export const DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 80,
  suppressMenu: false
};

/**
 * Predefined grid configurations for common use cases
 */
export const GRID_PRESETS = {
  /** Simple read-only grid */
  readonly: {
    ...DEFAULT_GRID_CONFIG,
    rowSelection: undefined,
    enableFiltering: false
  } as IsgGridConfig,

  /** Compact grid with smaller pagination */
  compact: {
    ...DEFAULT_GRID_CONFIG,
    pageSize: 5,
    pageSizeOptions: [5, 10, 25],
    height: '250px'
  } as IsgGridConfig,

  /** Full-height grid without pagination */
  fullHeight: {
    ...DEFAULT_GRID_CONFIG,
    pagination: false,
    height: '100%'
  } as IsgGridConfig,

  /** Single selection grid */
  singleSelect: {
    ...DEFAULT_GRID_CONFIG,
    rowSelection: 'single'
  } as IsgGridConfig
};

/**
 * Helper function to merge grid configurations
 */
export function mergeGridConfig(custom: Partial<IsgGridConfig>): IsgGridConfig {
  return { ...DEFAULT_GRID_CONFIG, ...custom };
}

/**
 * Convert IsgGridConfig to AG-Grid GridOptions
 */
export function toGridOptions(config: IsgGridConfig): Partial<GridOptions> {
  return {
    pagination: config.pagination,
    paginationPageSize: config.pageSize,
    paginationPageSizeSelector: config.pageSizeOptions,
    rowSelection: config.rowSelection,
    animateRows: config.animateRows,
    suppressCellFocus: false,
    enableCellTextSelection: config.enableCellTextSelection,
    suppressHorizontalScroll: config.suppressHorizontalScroll,
    overlayNoRowsTemplate: `<span class="ag-overlay-no-rows-center">${config.noRowsMessage}</span>`
  };
}
